import { useMemo } from "react";
import type { AppData } from "@/types";
import { buildAdvisorAccounts } from "@/utils/accounting";
import { formatCOP, formatNumber } from "@/utils/format";

interface AdvisorCarteraPanelProps {
  data: AppData;
}

// Cartera de asesores: cargos (entregas activas), abonos y saldo que debe cada
// asesor al administrador. La deuda se calcula con el valor de catálogo menos el
// descuento configurado del asesor (75% si está al 25%, 80% si está al 20%).
const AdvisorCarteraPanel = ({ data }: AdvisorCarteraPanelProps) => {
  const accounts = useMemo(
    () =>
      buildAdvisorAccounts(data)
        .filter((a) => a.charges > 0 || a.consignedUnits > 0 || a.paid > 0)
        .sort((a, b) => b.debt - a.debt),
    [data]
  );

  const totalCharges = accounts.reduce((a, x) => a + x.charges, 0);
  const totalPaid = accounts.reduce((a, x) => a + x.paid, 0);
  const totalDebt = accounts.reduce((a, x) => a + x.debt, 0);

  const cards = [
    {
      icon: "ri-file-list-3-line",
      label: "Total cargado a asesores",
      value: formatCOP(totalCharges),
      bubble: "bg-primary-500/25 text-primary-100",
    },
    {
      icon: "ri-hand-coin-line",
      label: "Total abonado por asesores",
      value: formatCOP(totalPaid),
      bubble: "bg-accent-500/20 text-accent-200",
    },
    {
      icon: "ri-user-received-line",
      label: "Saldo por cobrar",
      value: formatCOP(totalDebt),
      bubble: "bg-secondary-500/25 text-secondary-200",
    },
  ];

  return (
    <div className="glass-panel rounded-lg p-4 md:p-5">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-8 h-8 rounded-md bg-secondary-500/25 text-secondary-200 flex items-center justify-center">
          <i className="ri-user-shared-line" />
        </span>
        <h3 className="editable-title text-base font-bold">Cartera de Asesores</h3>
      </div>
      <p className="text-[11px] text-foreground-500 mb-4">
        Lo que debe cada asesor según sus entregas activas (precio de catálogo menos su descuento)
        y los abonos / pagos recibidos.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {cards.map((c) => (
          <div
            key={c.label}
            className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2.5 animate-fade-up"
          >
            <span className={`w-8 h-8 rounded-md flex items-center justify-center ${c.bubble}`}>
              <i className={`${c.icon} text-base`} />
            </span>
            <p className="text-[10px] text-foreground-500 mt-2">{c.label}</p>
            <p className="text-sm font-bold text-foreground-950 break-words">{c.value}</p>
          </div>
        ))}
      </div>

      {accounts.length === 0 ? (
        <p className="px-5 py-8 text-center text-xs text-foreground-500">
          Aún no hay movimientos de asesores.
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-foreground-500 border-b border-background-300/40">
                <th className="py-2 pr-3 font-semibold">Asesor</th>
                <th className="py-2 px-3 font-semibold">Margen</th>
                <th className="py-2 px-3 font-semibold">Consignado</th>
                <th className="py-2 px-3 font-semibold">Cargado</th>
                <th className="py-2 px-3 font-semibold">Abonado</th>
                <th className="py-2 pl-3 font-semibold text-right">Debe</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-300/20">
              {accounts.map((a) => (
                <tr key={a.advisor.id} className="text-xs">
                  <td className="py-2.5 pr-3">
                    <p className="font-semibold text-foreground-950 whitespace-nowrap">
                      {a.advisor.names} {a.advisor.surnames}
                    </p>
                    <p className="text-[10px] text-foreground-500">{a.advisor.city || "—"}</p>
                  </td>
                  <td className="py-2.5 px-3 text-foreground-600 whitespace-nowrap">{a.margin}%</td>
                  <td className="py-2.5 px-3 whitespace-nowrap">
                    <span className="text-secondary-200 font-semibold">
                      {formatCOP(a.consignedValue)}
                    </span>
                    <span className="block text-[10px] text-foreground-500">
                      {formatNumber(a.consignedUnits)} und
                    </span>
                  </td>
                  <td className="py-2.5 px-3 text-foreground-800 whitespace-nowrap">
                    {formatCOP(a.charges)}
                  </td>
                  <td className="py-2.5 px-3 text-accent-200 whitespace-nowrap">
                    {formatCOP(a.paid)}
                  </td>
                  <td
                    className={`py-2.5 pl-3 text-right font-bold whitespace-nowrap ${
                      a.debt > 0 ? "text-foreground-950" : "text-foreground-500"
                    }`}
                  >
                    {formatCOP(a.debt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdvisorCarteraPanel;